/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { PillLabel } from '@mpb-com/feather/components/atoms/pill-label';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent } from 'react';
import styled from 'styled-components';

import { WrappedLink } from '@toucan/client/global/nextjs';

import { ResponsiveImage } from '@toucan/client/blocks/components/responsive-image';

import { styles } from './article-tile.style';
import { ArticleTileProps, ArticleTileSkeletonProps } from './types';

const StyledArticleTile = styled.article`
    ${styles}
`;

export const ArticleTile: FunctionComponent<
    ArticleTileProps | ArticleTileSkeletonProps
> = (props) => {
    const { t } = useTranslation();
    const { isLoading, className } = props;

    function view() {
        if (isLoading) {
            return <ArticleTileSkeleton />;
        }

        const {
            thumbnail,
            title,
            teaser,
            publishedAt,
            link,
            categoryTag,
            imageSizes,
        } = props;

        return (
            <Fragment>
                <div className="image">
                    <ResponsiveImage
                        src={thumbnail}
                        alt={title}
                        width={500}
                        height={500}
                        sizes={imageSizes}
                    />
                    <PillLabel className="category">{categoryTag}</PillLabel>
                </div>
                <Heading className="title" size={HeadingSize.H4}>
                    {title}
                </Heading>
                <small className="publish-date">{publishedAt}</small>
                <p className="teaser">{teaser}</p>
                <WrappedLink
                    className="read-more"
                    href={link.templatePath}
                    as={link.href}
                    aria-label={t('Read full article')}
                >
                    {t('Read full article')} &gt;
                </WrappedLink>
            </Fragment>
        );
    }

    return (
        <StyledArticleTile className={className}>{view()}</StyledArticleTile>
    );
};

function ArticleTileSkeleton() {
    return (
        <Fragment>
            <Skeleton
                className="skeleton-image"
                variant={SkeletonVariant.BLOCK}
            />
            <Skeleton
                className="skeleton-title"
                variant={SkeletonVariant.TEXT}
                lineCount={3}
            />
            <Skeleton
                className="skeleton-publish-date"
                variant={SkeletonVariant.TEXT}
            />
            <Skeleton
                className="skeleton-teaser"
                variant={SkeletonVariant.TEXT}
                lineHeight={1.5}
                lineCount={4}
            />
            <Skeleton
                className="skeleton-read-more"
                variant={SkeletonVariant.TEXT}
            />
        </Fragment>
    );
}
